import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronDownIcon,
  ArrowRightOnRectangleIcon,
  UserCircleIcon
} from '@heroicons/react/24/outline';
import { UserAvatar } from './users/UserAvatar';

interface UserProfileDropdownProps {
  onLogout: () => void;
  userEmail?: string;
  userName?: string;
  avatarUrl?: string;
}

const UserProfileDropdown: React.FC<UserProfileDropdownProps> = ({
  onLogout,
  userEmail = "user@example.com",
  userName = "John Doe",
  avatarUrl
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Close dropdown on Escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen]);

  const handleLogout = () => {
    setIsOpen(false);
    onLogout();
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Profile Button — permanent lighter pill background so the button
          reads as a distinct interactive element against the dark title
          bar, not just floating text. */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 p-2 rounded-lg bg-gray-800 hover:bg-gray-700 border border-gray-700 transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-gray-900"
        aria-expanded={isOpen}
        aria-haspopup="true"
        aria-label="User profile menu"
      >
        {/* Photo (M72), else initials */}
        <UserAvatar name={userName} src={avatarUrl ?? null} className="w-8 h-8 text-xs border border-gray-700" />

        {/* Name and Chevron (hidden on mobile) */}
        <div className="hidden sm:flex items-center gap-1">
          <div className="text-left">
            <div className="text-sm font-medium text-white truncate max-w-32">
              {userName}
            </div>
          </div>
          <ChevronDownIcon
            className={`w-4 h-4 text-gray-400 transition-transform ${
              isOpen ? 'rotate-180' : 'rotate-0'
            }`}
          />
        </div>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          className="absolute right-0 mt-2 w-80 bg-white rounded-lg py-2 z-50"
          role="menu"
          aria-orientation="vertical"
          // Inline styles so the border + shadow render regardless of
          // whether Tailwind's JIT picked these exact class names up.
          style={{
            border: '1px solid #9ca3af', // gray-400
            boxShadow:
              '0 25px 50px -12px rgb(0 0 0 / 0.35), ' +
              '0 0 0 1px rgb(0 0 0 / 0.05)',
          }}
        >
          {/* User Info Header */}
          <div className="px-4 py-3 border-b border-gray-100">
            <div className="flex items-center gap-3">
              <UserAvatar name={userName} src={avatarUrl ?? null} className="w-10 h-10 text-sm border border-gray-200" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-900 truncate">
                  {userName}
                </p>
                <p className="text-sm text-gray-500 truncate">
                  {userEmail}
                </p>
              </div>
            </div>
          </div>

          {/* M73: own name, phones and home address */}
          <div className="pt-2">
            <Link
              to="/my-profile"
              onClick={() => setIsOpen(false)}
              className="flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors w-full text-left"
              role="menuitem"
            >
              <UserCircleIcon className="w-5 h-5 text-gray-400" />
              <div className="flex-1">
                <div className="font-medium">My profile</div>
                <div className="text-xs text-gray-500">Your name, phones and home address</div>
              </div>
            </Link>
          </div>

          {/* Logout Section */}
          <div className="pt-2">
            <button
              onClick={handleLogout}
              className="flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors w-full text-left"
              role="menuitem"
            >
              <ArrowRightOnRectangleIcon className="w-5 h-5 text-gray-400" />
              <div className="flex-1">
                <div className="font-medium">Sign Out</div>
                <div className="text-xs text-gray-500">Sign out of your account</div>
              </div>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserProfileDropdown;